import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./SBOSettings.css";
import { FiInfo } from "react-icons/fi";
import { HiOutlineArrowLeft } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { safetyObservationService, masterDataService, authService } from "../../../services";
import userService from "../../../services/userService";

const SBOSettings = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    facilityLevel: "",
    allowNonOfficeHours: true,
    refNoFormat: "",
    nameEmployees: true,
    teamObservations: true,
    submissionHours: "",
    maxDuration: "",
    assignOpenObservation: "",
    defaultDueDateSeverity: true,
    allowMediaUpload: true,
    allowMediaUploadAction: true,
  });

  const [notificationData, setNotificationData] = useState({
    notifyOnSubmission: "",
    notifyOnActionAssignment: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotification, setSavingNotification] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const data = await safetyObservationService.getSBOSettings();

        // Map API response to form state
        setFormData({
          facilityLevel:
            data.facilityLevel != null ? String(data.facilityLevel) : "",
          allowNonOfficeHours: data.allowNonOfficeHours ?? true,
          refNoFormat: data.refNoFormat || "",
          nameEmployees: data.nameEmployees ?? true,
          teamObservations: data.teamObservations ?? true,
          submissionHours:
            data.submissionHours != null ? String(data.submissionHours) : "",
          maxDuration: data.maxDuration != null ? String(data.maxDuration) : "",
          assignOpenObservation: data.assignOpenObservation || "",
          defaultDueDateSeverity: data.defaultDueDateSeverity ?? true,
          allowMediaUpload: data.allowMediaUpload ?? true,
          allowMediaUploadAction: data.allowMediaUploadAction ?? true,
        });

        setNotificationData({
          notifyOnSubmission: data.notifyOnSubmission || "",
          notifyOnActionAssignment: data.notifyOnActionAssignment || "",
        });
      } catch (error) {
        console.error("Error loading SBO settings:", error);
        // Silent fail on load - will use default state
      }

      try {
        const tenantId = authService.getTenantId();
        const [usersData, rolesData] = await Promise.all([
          userService.getAllUsers(),
          masterDataService.getRoles(tenantId)
        ]);
        if (usersData && Array.isArray(usersData)) {
          setUsers(
            usersData.filter(
              (u) =>
                u.status === "Active" ||
                u.Status === "Active" ||
                (!u.status && !u.Status),
            ),
          );
        }
        setRoles(rolesData || []);
      } catch (err) {
        console.error("Error loading extra data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNotificationChange = (e) => {
    const { name, value } = e.target;
    setNotificationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Save Configuration settings
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        facilityLevel: formData.facilityLevel
          ? parseInt(formData.facilityLevel, 10)
          : null,
        allowNonOfficeHours: formData.allowNonOfficeHours,
        refNoFormat: formData.refNoFormat || null,
        nameEmployees: formData.nameEmployees,
        teamObservations: formData.teamObservations,
        submissionHours: formData.submissionHours
          ? parseInt(formData.submissionHours, 10)
          : null,
        maxDuration: formData.maxDuration
          ? parseInt(formData.maxDuration, 10)
          : null,
        assignOpenObservation: formData.assignOpenObservation || null,
        defaultDueDateSeverity: formData.defaultDueDateSeverity,
        allowMediaUpload: formData.allowMediaUpload,
        allowMediaUploadAction: formData.allowMediaUploadAction,
        // Also include current notification data to preserve it
        notifyOnSubmission: notificationData.notifyOnSubmission || null,
        notifyOnActionAssignment:
          notificationData.notifyOnActionAssignment || null,
        enableEmailNotifications: true,
      };

      await safetyObservationService.saveSBOSettings(payload);
      toast.success("Configuration settings saved successfully!");
    } catch (err) {
      console.error("Error saving configuration settings:", err);
      toast.error("Failed to save configuration settings");
    } finally {
      setSaving(false);
    }
  };

  // Save Notification settings
  const handleNotificationSave = async (e) => {
    e.preventDefault();
    try {
      setSavingNotification(true);
      const payload = {
        // Include current config data to preserve it
        facilityLevel: formData.facilityLevel
          ? parseInt(formData.facilityLevel, 10)
          : null,
        allowNonOfficeHours: formData.allowNonOfficeHours,
        refNoFormat: formData.refNoFormat || null,
        nameEmployees: formData.nameEmployees,
        teamObservations: formData.teamObservations,
        submissionHours: formData.submissionHours
          ? parseInt(formData.submissionHours, 10)
          : null,
        maxDuration: formData.maxDuration
          ? parseInt(formData.maxDuration, 10)
          : null,
        assignOpenObservation: formData.assignOpenObservation || null,
        defaultDueDateSeverity: formData.defaultDueDateSeverity,
        allowMediaUpload: formData.allowMediaUpload,
        allowMediaUploadAction: formData.allowMediaUploadAction,
        // Notification data
        notifyOnSubmission: notificationData.notifyOnSubmission || null,
        notifyOnActionAssignment:
          notificationData.notifyOnActionAssignment || null,
        enableEmailNotifications: true,
      };

      await safetyObservationService.saveSBOSettings(payload);
      toast.success("Notification settings saved successfully!");
    } catch (err) {
      console.error("Error saving notification settings:", err);
      toast.error("Failed to save notification settings");
    } finally {
      setSavingNotification(false);
    }
  };

  if (loading) {
    return (
      <div className="sbo-settings-container">
        <div className="sbo-settings-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="sbo-settings-container">
      <div className="sbo-settings-header">
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          title="Go Back"
        >
          <HiOutlineArrowLeft />
        </button>
        <h2>Safety Configuration</h2>
      </div>

      <form onSubmit={handleSubmit} className="sbo-settings-form">
        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Up to what Facility level you want to perform observation rounds"
            />
            <label>
              Up to what Facility level you want to perform observation rounds
            </label>
          </div>
          <div className="input-col">
            <select
              name="facilityLevel"
              value={formData.facilityLevel}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select Level</option>
              <option value="1">Level 1</option>
              <option value="2">Level 2</option>
              <option value="3">Level 3</option>
              <option value="4">Level 4</option>
              <option value="5">Level 5</option>
              <option value="6">Level 6</option>
            </select>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Allow observation rounds to be conducted on Non office hours"
            />
            <label>
              Allow observation rounds to be conducted on Non office hours
            </label>
          </div>
          <div className="input-col">
            <label className="switch">
              <input
                type="checkbox"
                name="allowNonOfficeHours"
                checked={formData.allowNonOfficeHours}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Do you want to name the employees during observations"
            />
            <label>Do you want to name the employees during observations</label>
          </div>
          <div className="input-col">
            <label className="switch">
              <input
                type="checkbox"
                name="nameEmployees"
                checked={formData.nameEmployees}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Do you conduct observations in team"
            />
            <label>Do you conduct observations in team</label>
          </div>
          <div className="input-col">
            <label className="switch">
              <input
                type="checkbox"
                name="teamObservations"
                checked={formData.teamObservations}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Observation should be submitted within ___ hours"
            />
            <label>Observation should be submitted within ___ hours</label>
          </div>
          <div className="input-col">
            <input
              type="number"
              name="submissionHours"
              value={formData.submissionHours}
              onChange={handleInputChange}
              placeholder="Answer"
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Max Observation round should be (min) duration"
            />
            <label>Max Observation round should be (min) duration</label>
          </div>
          <div className="input-col">
            <input
              type="number"
              name="maxDuration"
              value={formData.maxDuration}
              onChange={handleInputChange}
              placeholder="Answer"
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Assign Open observation by default to"
            />
            <label>Assign Open observation by default to</label>
          </div>
          <div className="input-col">
            <select
              name="assignOpenObservation"
              value={formData.assignOpenObservation}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select head for</option>
              <option value="dept_head">Department head</option>
              <option value="area_owner">Area owner</option>
              <option value="safety_head">Safety head</option>
            </select>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Do you want to set default due date of Open action based on Severity"
            />
            <label>
              Do you want to set default due date of Open action based on
              Severity
            </label>
          </div>
          <div className="input-col">
            <label className="switch">
              <input
                type="checkbox"
                name="defaultDueDateSeverity"
                checked={formData.defaultDueDateSeverity}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Do you allow upload of media (Image/Video/Document) for observation round conducted"
            />
            <label>
              Do you allow upload of media (Image/Video/Document) for
              observation round conducted
            </label>
          </div>
          <div className="input-col">
            <label className="switch">
              <input
                type="checkbox"
                name="allowMediaUpload"
                checked={formData.allowMediaUpload}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        <div className="form-group-row">
          <div className="label-col">
            <FiInfo
              className="info-icon"
              title="Do you allow upload media for open actions"
            />
            <label>Do you allow upload media for open actions</label>
          </div>
          <div className="input-col">
            <label className="switch">
              <input
                type="checkbox"
                name="allowMediaUploadAction"
                checked={formData.allowMediaUploadAction}
                onChange={handleInputChange}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        <div className="form-actions-row">
          <button type="submit" className="btn-success" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>

      {/* Notification Setting Section */}
      <div className="sbo-settings-section">
        <div className="sbo-settings-header">
          <h2>Notification Setting</h2>
        </div>

        <form onSubmit={handleNotificationSave} className="sbo-settings-form">
          <div className="form-group-row">
            <div className="label-col">
              <FiInfo
                className="info-icon"
                title="Who should be notified when observation is submitted"
              />
              <label>
                Who should be notified when observation is submitted via email &
                Notification
              </label>
            </div>
            <div className="input-col">
              <select
                name="notifyOnSubmission"
                value={notificationData.notifyOnSubmission}
                onChange={handleNotificationChange}
                className="form-control"
              >
                <option value="">Select head for</option>
                <optgroup label="Roles">
                  {roles.map((r) => (
                    <option key={r.roleId || r.id} value={r.roleName || r.name}>
                      {r.roleName || r.name}
                    </option>
                  ))}
                </optgroup>

              </select>
            </div>
          </div>

          <div className="form-group-row">
            <div className="label-col">
              <FiInfo
                className="info-icon"
                title="Who should be notified when observation action is assigned to employee"
              />
              <label>
                Who should be notified when observation action is assigned to
                employee via email & Notification
              </label>
            </div>
            <div className="input-col">
              <select
                name="notifyOnActionAssignment"
                value={notificationData.notifyOnActionAssignment}
                onChange={handleNotificationChange}
                className="form-control"
              >
                <option value="">Select head for</option>
                <optgroup label="Roles">
                  {roles.map((r) => (
                    <option key={r.roleId || r.id} value={r.roleName || r.name}>
                      {r.roleName || r.name}
                    </option>
                  ))}
                </optgroup>

              </select>
            </div>
          </div>

          <div className="form-actions-row">
            <button
              type="submit"
              className="btn-success"
              disabled={savingNotification}
            >
              {savingNotification ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SBOSettings;
